import '@backstage/cli/asset-types';
import '@scania/tegel-lite/traton-variables.css';
import '@scania/tegel-lite/global.css';
import '@scania/tegel-lite/tl-icon.css';
import '@backstage/ui/css/styles.css';
import './styles/index.css';
import app from './App';
import { createRoot } from 'react-dom/client';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs, type ConsoleApiName } from '@datadog/browser-logs';

/**
 * Dev-only: hide noisy MUI v4 / layout warnings so they don't spam console/RUM,
 * and filter webpack-dev-server websocket errors.
 */
/* eslint-disable no-console */
function patchConsoleForDevNoise() {
  const originalError = console.error.bind(console);
  const originalWarn = console.warn.bind(console);

  const patterns = [
    /findDOMNode is deprecated/i,
    /validateDOMNesting/i,
    /<p> cannot appear as a descendant of <p>/i,
  ];

  const argToText = (a: any): string => {
    if (typeof a === 'string') return a;
    if (a && typeof a.message === 'string') return a.message;
    try {
      return JSON.stringify(a);
    } catch {
      return '';
    }
  };

  // Suppression for webpack-dev-server websocket errors
  const isDevWsError = (args: any[]) =>
    args.some(a => {
      if (!a || typeof a !== 'object') return false;
      const t = (a as any).type;
      const tgt = (a as any).currentTarget ?? (a as any).target;
      const tgtName = tgt?.constructor?.name ?? '';
      return (
        (t === 'error' || (a as any).isTrusted === true) &&
        /WebSocket/i.test(tgtName)
      );
    });

  const shouldSuppress = (args: any[]) => {
    const text = args.map(argToText).join(' ');
    return patterns.some(rx => rx.test(text)) || isDevWsError(args);
  };

  console.error = (...args: any[]) => {
    if (shouldSuppress(args)) return;
    originalError(...args);
  };

  console.warn = (...args: any[]) => {
    if (shouldSuppress(args)) return;
    originalWarn(...args);
  };
}

if (process.env.NODE_ENV === 'development') {
  patchConsoleForDevNoise();
  setTimeout(patchConsoleForDevNoise, 0);
}
/* eslint-enable no-console */

// Read a meta tag value
function meta(name: string): string | undefined {
  return (
    document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content ||
    undefined
  );
}

// Datadog SDK union types for stricter casting
type Privacy = 'mask' | 'mask-user-input' | 'allow';
type Site =
  | 'datadoghq.com'
  | 'datadoghq.eu'
  | 'us3.datadoghq.com'
  | 'us5.datadoghq.com'
  | 'ap1.datadoghq.com'
  | 'ddog-gov.com';

// Narrow unknown/optional to a non-empty string for TS
function isNonEmptyString(x: unknown): x is string {
  return typeof x === 'string' && x.length > 0;
}
function randomId(): string {
  return (
    Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2, 6)
  );
}

function getOrCreateAnonId(): string {
  try {
    const k = 'pa_anonymous_id';
    let v = localStorage.getItem(k);
    if (!v) {
      v = `anon_${randomId()}`;
      localStorage.setItem(k, v);
    }
    return v;
  } catch {
    // localStorage not available; fallback ephemeral id
    return `anon_${randomId()}`;
  }
}

(function initDatadog() {
  const ddApplicationId = meta('dd-app-id');
  const ddClientToken = meta('dd-client-token');

  if (!isNonEmptyString(ddApplicationId) || !isNonEmptyString(ddClientToken)) {
    return;
  }

  const applicationId = ddApplicationId;
  const clientToken = ddClientToken;

  const allowedSites: readonly Site[] = [
    'datadoghq.com',
    'datadoghq.eu',
    'us3.datadoghq.com',
    'us5.datadoghq.com',
    'ap1.datadoghq.com',
    'ddog-gov.com',
  ];

  const siteRaw = meta('dd-site') ?? 'datadoghq.eu';
  const site: Site = allowedSites.includes(siteRaw as Site)
    ? (siteRaw as Site)
    : 'datadoghq.eu';

  const service = meta('dd-service') ?? 'developer-portal';
  const environment = meta('dd-env') ?? '';
  const sessionSampleRate = Number(meta('dd-session-sample-rate') ?? 100);
  const sessionReplaySampleRate = Number(
    meta('dd-session-replay-sample-rate') ?? 0,
  );
  const defaultPrivacyLevel = (meta('dd-default-privacy') ??
    'mask-user-input') as Privacy;

  // Initialize Logs ASAP (we use it for informational messages)
  const forwardConsoleLogs: 'all' | ConsoleApiName[] =
    process.env.NODE_ENV === 'development'
      ? (['error'] as ConsoleApiName[])
      : 'all';

  datadogLogs.init({
    clientToken,
    site,
    service,
    env: environment,
    forwardErrorsToLogs: true,
    forwardConsoleLogs,
  });

  // Only enable RUM for prod and ephemeral environments
  const enableRum = environment === 'prod' || environment.startsWith('review/');
  if (!enableRum) {
    datadogLogs.logger.info('[RUM] disabled for environment', {
      env: environment || '(none)',
    });
    return;
  }

  datadogRum.init({
    applicationId,
    clientToken,
    site,
    service,
    env: environment,
    sessionSampleRate: Number.isFinite(sessionSampleRate)
      ? sessionSampleRate
      : 100,
    sessionReplaySampleRate: Number.isFinite(sessionReplaySampleRate)
      ? sessionReplaySampleRate
      : 0,
    defaultPrivacyLevel,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
  });

  // Start an initial view immediately so PA has a page-view to attach to.
  datadogRum.startView('app_loaded');

  if (
    (Number.isFinite(sessionReplaySampleRate) ? sessionReplaySampleRate : 0) > 0
  ) {
    datadogRum.startSessionReplayRecording();
  }

  // Identify user: try authenticated user; else fall back to stable anonymous id.
  datadogRum.onReady(async () => {
    const anonymousId = getOrCreateAnonId();
    datadogRum.setUser({ id: anonymousId });
    datadogRum.addAction('pa_user_identified_fallback', {
      id: anonymousId,
      anonymous: true,
    });
  });

  datadogLogs.logger.info('[RUM] initialized', {
    site,
    env: environment,
    service,
  });
})();

createRoot(document.getElementById('root')!).render(app);
